import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { XMarkIcon } from '@heroicons/react/24/outline';
import DatePicker from '../ui/DatePicker';

const MailingModal = ({ isOpen, onClose, onSave, initialData, referenceData }) => {
  const [formData, setFormData] = useState({
    mailing_name: '',
    mailing_goal: '',
    planned_date: '',
    target_market: '',
    b2b_b2c: 'b2b',
    theme: '',
    redirect_link: '',
    visual_design_status: 'waiting',
    text_content_status: 'waiting',
    responsible_text: '',
    responsible_visual: '',
    responsible_publish: '',
    general_status: 'waiting',
    notes: ''
  });

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          mailing_name: initialData.mailing_name || '',
          mailing_goal: initialData.mailing_goal || '',
          planned_date: initialData.planned_date || '',
          target_market: initialData.target_market || '',
          b2b_b2c: initialData.b2b_b2c || 'b2b',
          theme: initialData.theme || '',
          redirect_link: initialData.redirect_link || '',
          visual_design_status: initialData.visual_design_status || 'waiting',
          text_content_status: initialData.text_content_status || 'waiting',
          responsible_text: initialData.responsible_text || '',
          responsible_visual: initialData.responsible_visual || '',
          responsible_publish: initialData.responsible_publish || '',
          general_status: initialData.general_status || 'waiting',
          notes: initialData.notes || ''
        });
      } else {
        setFormData({
          mailing_name: '',
          mailing_goal: '',
          planned_date: '',
          target_market: '',
          b2b_b2c: 'b2b',
          theme: '',
          redirect_link: '',
          visual_design_status: 'waiting',
          text_content_status: 'waiting',
          responsible_text: '',
          responsible_visual: '',
          responsible_publish: '',
          general_status: 'waiting',
          notes: ''
        });
      }
    }
  }, [isOpen, initialData]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      id: initialData?.id
    };
    onSave(dataToSave);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-24 px-4 overflow-hidden">
      {/* Backdrop - Blur removed, just opacity */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={(e) => {
          // DatePicker calendar popup'ına veya modal içeriğine tıklanırsa kapatma
          const target = e.target;
          const isDatePickerCalendar = target.closest('[class*="z-[10000]"]') ||
            target.closest('.absolute.top-full');

          if (e.target === e.currentTarget && !isDatePickerCalendar) {
            onClose();
          }
        }}
      />

      {/* Modal Container - Matches other modals design */}
      <div
        className="relative z-10 w-full max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[85vh] transform transition-all animate-in fade-in zoom-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {initialData ? 'Mailing Düzenle' : 'Yeni Mailing Oluştur'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          <form id="mailing-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mailing Name</Label>
                <Input
                  value={formData.mailing_name}
                  onChange={(e) => handleChange('mailing_name', e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mailing Goal</Label>
                <Input
                  value={formData.mailing_goal}
                  onChange={(e) => handleChange('mailing_goal', e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Planned Date</Label>
                <DatePicker
                  value={formData.planned_date}
                  onChange={(e) => handleChange('planned_date', e.target?.value || e)}
                  placeholder="Tarih Seç"
                  name="planned_date"
                  className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Market</Label>
                <select
                  value={formData.target_market}
                  onChange={(e) => handleChange('target_market', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  {referenceData?.targetMarkets?.map(market => (
                    <option key={market.id || market.value} value={market.value || market.name}>
                      {market.label || market.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">B2B/B2C</Label>
                <select
                  value={formData.b2b_b2c}
                  onChange={(e) => handleChange('b2b_b2c', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="b2b">B2B</option>
                  <option value="b2c">B2C</option>
                  <option value="both">B2B & B2C</option>
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</Label>
                <Input
                  value={formData.theme}
                  onChange={(e) => handleChange('theme', e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="space-y-1 md:col-span-3">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Yönlendirilecek Link</Label>
                <Input
                  value={formData.redirect_link}
                  onChange={(e) => handleChange('redirect_link', e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>

              {/* Status Grupları Yan Yana */}
              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Visual Design Status</Label>
                <select
                  value={formData.visual_design_status}
                  onChange={(e) => handleChange('visual_design_status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="waiting">Beklemede</option>
                  <option value="in_progress">Yapılıyor</option>
                  <option value="completed">Hazır</option>
                  <option value="cancelled">İptal Edildi</option>
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Text Content Status</Label>
                <select
                  value={formData.text_content_status}
                  onChange={(e) => handleChange('text_content_status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="waiting">Beklemede</option>
                  <option value="in_progress">Yapılıyor</option>
                  <option value="completed">Hazır</option>
                  <option value="cancelled">İptal Edildi</option>
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status in General</Label>
                <select
                  value={formData.general_status}
                  onChange={(e) => handleChange('general_status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="waiting">Beklemede</option>
                  <option value="in_progress">Yapılıyor</option>
                  <option value="completed">Tamamlandı</option>
                  <option value="cancelled">İptal Edildi</option>
                </select>
              </div>

              {/* Sorumlu Kişiler Yan Yana */}
              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Responsible (Text)</Label>
                <select
                  value={formData.responsible_text}
                  onChange={(e) => handleChange('responsible_text', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  {referenceData?.users?.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Responsible (Visual)</Label>
                <select
                  value={formData.responsible_visual}
                  onChange={(e) => handleChange('responsible_visual', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  {referenceData?.users?.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Responsible (Publish)</Label>
                <select
                  value={formData.responsible_publish}
                  onChange={(e) => handleChange('responsible_publish', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  {referenceData?.users?.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1 md:col-span-3">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Not (Notes)</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Ek notlar..."
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="px-4 py-2 text-sm"
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
          >
            {initialData ? 'Kaydet' : 'Oluştur'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MailingModal;
